import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';

// @mui
import { LoadingButton } from '@mui/lab';
import { styled } from '@mui/material/styles';
import { Alert, AlertTitle, Box, Switch, Typography } from '@mui/material';

import UsersListSvg from '../assets/svgs/usersList';
import { BlankCard } from '../components/cards/blankCard';
import pageTitle from '../utils/pageTitles.json';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { renderErrorMessage, renderSuccessMessage } from 'src/lib/toastMessage';
import { axiosGetCall, axiosPostCall } from 'src/services/apisCall';
import { jwtDecode } from 'jwt-decode';

const RetrySettingSchema = Yup.object().shape({
  status: Yup.boolean().required(),
  retryCount: Yup.number()
    .when('status', {
      is: true,
      then: (schema) =>
        schema
          .typeError('Retry count must be a number')
          .integer('Retry count must be a whole number')
          .min(1, 'Retry count must be greater than 0')
          .required('Retry count is required'),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
  retryInterval: Yup.number().when('status', {is: true,then: (schema) => schema
          .typeError('Retry interval must be a number')
          .integer('Retry interval must be a whole number')
        .min(60, 'Retry interval must be greater than 60')
          .required('Retry interval is required'),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
});

// home main section container.
const MainSection = styled('div')(() => ({
  padding: '64px 20px 0px 88px',
  backgroundColor: 'rgb(240, 240, 240)',
  height: '100vh',
  overflow: 'hidden',
}));

// Show home heading and filters.
const HeadingSection = styled('div')(() => ({
  width: '100%',
  padding: '20px 0px',
  display: 'flex',
  justifyContent: 'space-between',
  flexDirection: 'row',
}));

const HeadingContentSection = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '15px',
  alignItems: 'center',
  justifyContent: 'center',
}));

const DetailsListSection = styled('div')(() => ({
  width: '100%',
  height: 'calc(100vh - 155px)',
  overflow: 'hidden',
  overflowY: 'auto',
  padding: '20px',
}));

const SwitchButton = styled(Switch)(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#65C466',
        opacity: 1,
        border: 0,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
  },
  '& .MuiSwitch-track': {
    borderRadius: 13,
    backgroundColor: theme.palette.grey[500],
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 300,
    }),
  },
}));

const RetrySetting = () => {
  const [isLoading, setIsLoading] = useState(true);
    const { email } = jwtDecode(localStorage.getItem('token'));

  const methods = useForm({
    resolver: yupResolver(RetrySettingSchema),
    defaultValues: {
      status: false,
      retryCount: '',
      retryInterval: '',
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isSubmitting },
  } = methods;

  const globalRetryEnabled = watch('status');
  const onSubmit = async (data) => {
     const retryEnabled = Boolean(data.status);
    try {
    const payload = {
      retry: retryEnabled,
      retry_count: retryEnabled ? Number(data.retryCount) : 0,
      retry_interval: retryEnabled ? Number(data.retryInterval) : 0,
      actionByEmail: email,
    };
      const result = await axiosPostCall('updateRetrySetting', payload);
      renderSuccessMessage(result?.message || 'Retry setting updated successfully');
      getRetryData(); // Refresh the data after successful update
    } catch (error) {
      console.error('Update Retry Setting', error);
      renderErrorMessage(error?.message);
    }
  };

      const getRetryData = async () => {
      try {
        const result = await axiosGetCall('getRetrySetting');
        const retrySetting = result?.retry_setting;

        reset({
          status:
            retrySetting?.retry === true ||
            retrySetting?.retry === 1 ||
            retrySetting?.retry === '1' ||
            retrySetting?.retry === 'true',
          retryCount: retrySetting?.retry_count ?? '',
          retryInterval: retrySetting?.retry_interval ?? '',
        });
      } catch (error) {
        console.error('Retry Setting', error);
        renderErrorMessage(error?.message);
      } finally {
        setIsLoading(false);
      }
    };

  useEffect(() => {
    getRetryData();
  }, []);

  return (
    <>
      <Helmet>
        <title>{pageTitle?.retrySetting}</title>
      </Helmet>

      <MainSection>
        <HeadingSection>
          <HeadingContentSection>
            <Box sx={{ height: '16px', mt: '-10px' }}>
              <UsersListSvg />
            </Box>
            <Typography variant="h5" sx={{ color: 'grey.10', fontWeight: 600 }}>
              Retry Settings
            </Typography>
          </HeadingContentSection>
        </HeadingSection>

        <BlankCard
          sx={{
            height: '100%',
            width: '100%',
            padding: '0px',
            overflow: 'hidden',
            backgroundColor: 'background.paper',
          }}
        >
          <DetailsListSection className="reportListScroll">
            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  mx: '6px',
                  p: '16px',
                  maxWidth: '70%',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '8px',
                }}
              >
                <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600 }}>
                  Global Retry
                </Typography>

                <Alert
                  severity="info"
                  variant="outlined"
                  sx={{
                    alignItems: 'flex-start',
                    borderRadius: 2,
                    borderColor: 'info.light',
                    bgcolor: 'rgba(2, 136, 209, 0.06)',
                    '& .MuiAlert-icon': { color: 'info.main', pt: '4px' },
                  }}
                >
                  <AlertTitle sx={{ mb: 0.5, fontWeight: 600 }}>Retry behavior</AlertTitle>
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    When Global Retry is enabled, failed SMS messages from all users and campaigns
                    will be retried while the operator-side delivery report is pending. Retries use
                    the count and interval configured below.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, lineHeight: 1.6 }}>
                    Retries are triggered only for the Transactional and Implicit routes.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, lineHeight: 1.6 }}>
                    For campaigns created using the Reschedule option, the retry document will also
                    be considered during rescheduling.
                  </Typography>
                </Alert>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Typography sx={{ color: 'grey.700', fontWeight: 500 }}>
                    Enable Global Retry
                  </Typography>
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <SwitchButton
                        {...field}
                        checked={field.value}
                        onChange={(event) => field.onChange(event.target.checked)}
                        disabled={isLoading || isSubmitting}
                        inputProps={{ 'aria-label': 'Global retry setting' }}
                      />
                    )}
                  />
                  <Typography
                    sx={{
                      color: globalRetryEnabled ? 'success.main' : 'text.secondary',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      minWidth: '70px',
                    }}
                  >
                    {isLoading ? 'Loading...' : globalRetryEnabled ? 'Active' : 'Not Active'}
                  </Typography>
                </Box>

                {globalRetryEnabled && (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '16px',
                      pt: '4px',
                      flexWrap: 'wrap',
                    }}
                  >
                    <RHFTextField
                      name="retryCount"
                      label="Retry Count"
                      type="number"
                      size="small"
                      placeholder="Enter retry count"
                      disabled={isSubmitting}
                      sx={{ width: { xs: '100%', sm: '240px' } }}
                    />
                    <RHFTextField
                      name="retryInterval"
                      label="Retry Interval (seconds)"
                      type="number"
                      size="small"
                      placeholder="Enter interval in seconds"
                      disabled={isSubmitting}
                      sx={{ width: { xs: '100%', sm: '240px' } }}
                    />
                  </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: '4px' }}>
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    loading={isSubmitting}
                    disabled={isLoading}
                    sx={{ minWidth: '100px' }}
                  >
                    Save
                  </LoadingButton>
                </Box>
              </Box>
            </FormProvider>
          </DetailsListSection>
        </BlankCard>
      </MainSection>
    </>
  );
};

export default RetrySetting;
